import { makeAutoObservable } from "mobx";
import type { SubjectsApi } from "@/api/subjects";
import { browserStorage, type CacheStorage, readListCache, writeListCache } from "./listCache";
import { Resource } from "./Resource";

const LIST_CACHE_KEY = "hp_subjects_v2";
const LIST_CACHE_MAX_AGE = 1000 * 60 * 60 * 24; // 24 hours

/** The fokusområden: the list of subjects, and one subject with its episodes in listening order. */
export class SubjectStore {
    readonly list: Resource<Subject[]>;
    readonly detail: Resource<Subject, string>;

    constructor(
        private readonly api: SubjectsApi,
        private readonly storage: CacheStorage | null = browserStorage()
    ) {
        this.list = new Resource(() => this.fetchList(), {
            initial: readListCache<Subject[]>(this.storage, LIST_CACHE_KEY, LIST_CACHE_MAX_AGE),
        });
        this.detail = new Resource((slug) => this.api.bySlug(slug));

        makeAutoObservable<SubjectStore, "api" | "storage">(this, {
            api: false,
            storage: false,
            list: false,
            detail: false,
        });
    }

    get subjects(): Subject[] {
        return this.list.data ?? [];
    }

    get subjectsLoaded() {
        return this.list.loaded;
    }

    get subject(): Subject | null {
        return this.detail.data ?? null;
    }

    isOpen(slug: string) {
        return this.detail.isFor(slug) && this.detail.loaded;
    }

    /** Loads a subject. Rejects when it cannot be loaded, so the page can show its error state. */
    async open(slug: string) {
        await this.detail.load(slug);
        if (this.detail.status === "error") {
            throw this.detail.error;
        }
    }

    /** Loads the list unless it is loaded or loading. Rejects when it cannot be loaded, so a page can show its error state. */
    async loadList() {
        await this.list.load();
        if (this.list.status === "error") {
            throw this.list.error;
        }
    }

    private async fetchList(): Promise<Subject[]> {
        const subjects = await this.api.list();
        writeListCache(this.storage, LIST_CACHE_KEY, subjects);
        return subjects;
    }
}
